import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Redux
import { NgRedux } from '@angular-redux/store';
import { IAppState } from '../../core/store/store';

// Core
import { BoolResult, IBoolResult } from '../../core/interfaces/bool-result';
import { ErrorMessage } from '../../core/model/error-message';

// Config

// Own feature
import { HttpResponse } from '@angular/common/http';
import { Action } from 'redux';
import { ConfirmDialogsService } from '../../core/components/confirm-dialog/service/confirm-dialog.service';
import { navigationConfig } from '../config/navigation.config';
import { NAV_DISPLAY_OPTIONS_INITIAL_STATE } from '../model/nav-display-options.interface';
import { INavElementsEdge } from '../model/nav-elements-edgegroup';
import { INavElementsNode } from '../model/nav-elements-nodegroup';
import { INavGraph2SelResult } from '../model/nav-graph2-sel-result.interface';
import { INavNewEdgeAndElement } from '../model/nav-new-edge-element.interface';
import { INavRequestOption } from '../model/nav-request-option.interface';
import { EdgeDirectionBKWD } from '../model/nav.types';
import { NavigationDatahubService } from '../service/navigation-datahub.service';
import {
    navigationStoreConfig, navigationStoreResult1stDetail,
    navigationStoreResult1stExtended, navigationStoreResult1stInit, navigationStoreResult1stInitialValue, navigationStoreResult2stDetail,
    navigationStoreResult2stExtended, navigationStoreResult2stInit, navigationStoreResult2stInitialValue,
    navigationStoreSchema
} from '../store/actions';

type validatorType = (fs: any) => ResponseError | undefined | Promise<ResponseError | undefined>;
type innerCB<T> = () => Promise<T>;
type actionGenerator<T> = (fs: T) => Action;


interface IErrorResponse {
    code: string;
    uiMessage?: string;
}

export interface IChecker {
    id: string;
    name: string;
}

function isErrorResponse(fs: any): fs is IErrorResponse {
    return fs && fs.hasOwnProperty(navigationConfig.loadRespondErrorIdentifier);
}

class ResponseError extends Error implements IErrorResponse {
    public code;
    public uiMessage;

    constructor(code: string, uiMessage: string) {
        super(uiMessage || code);
        this.code = code;
        this.uiMessage = uiMessage;
    }
}

function noEmptyResult(fs: any): ResponseError | undefined {
    if (fs.result && !fs.result.length) {
        return new ResponseError(
            'navigation/NO_DATA',
            'Die Suchanfrage ergab keine Treffer');
    }
    return undefined;
}

function arrayResponse(fs: any): ResponseError | undefined {
    if (typeof fs === 'object' && fs[0]) {
        return undefined;
    }

    return new ResponseError(
        'navigation/NO_DATA',
        'Unbekanntes Datenformat');
}

function rootOrResultResponse(fs: any): ResponseError | undefined {
    if (fs.hasOwnProperty(navigationConfig.loadRespondDataRootIdentifier)
        || fs.hasOwnProperty(navigationConfig.loadRespondDataVirtualRootIdentifier)) {
        return undefined;
    }

    return new ResponseError(
        'navigation/NO_DATA',
        'Unbekanntes Datenformat');
}

function rootResponse(fs: any): ResponseError | undefined {
    if (fs.hasOwnProperty(navigationConfig.loadRespondDataRootIdentifier)) {
        return undefined;
    }

    return new ResponseError(
        'navigation/NO_DATA',
        'Unbekanntes Datenformat');
}

function dataResponse(fs: any): ResponseError | undefined {
    if (fs.data) {
        return undefined;
    }
    return new ResponseError(
        'navigation/NO_DATA',
        'Unbekanntes Datenformat');
}

function noEmptyResponse(fs: any): ResponseError | undefined {
    if (!fs) {
        return new ResponseError(
            'navigation/NO_DATA',
            'Keine Daten erhalten oder unbekanntes Datenformat');
    }
    return undefined;
}

@Injectable()
export class NavigationService {

    constructor(
        private _router: Router,
        private _store: NgRedux<IAppState>,
        private _dataHubService: NavigationDatahubService,
        private _confirmDialogsService: ConfirmDialogsService
    ) { }

    private async inner<T>(cb: innerCB<T>, validators?: validatorType[]): Promise<T> {
        try {
            const fs = await cb();

            if (validators) {
                for (const validator of validators) {
                    const result = await validator(fs);
                    if (result) {
                        throw result;
                    }
                }
            }

            return fs;
        } catch (err) {
            // TODO: string in Config !!!
            if (err.code === 'HttpErrorResponse' && (!err.errorObject || err.errorObject.error.code !== 'DATA/validation')) {
                if (isErrorResponse(err.errorObject.error)) {
                    throw new ResponseError(err.errorObject.error.code, err.errorObject.error.uiMessage);
                }
            }
            throw err;
        }
    }

    private async ResponseHandler<T, TR>(params: {
        cb: innerCB<T>,
        onSuccess: (fs: T) => TR,
        onFail?: (err) => TR | undefined,
        validators?: validatorType[]
    }): Promise<TR> {
        try {
            const fs = await this.inner(params.cb, params.validators);
            return params.onSuccess(fs);
        } catch (err) {
            if (params.onFail) {
                const retVal = params.onFail(err);
                if (retVal) {
                    return retVal;
                }
            }
            throw err;
        }
    }


    public BoolResponseHandler<T>(
        cb: innerCB<T>,
        onSuccess?: actionGenerator<T>,
        onFail?: actionGenerator<T>,
        validators?: validatorType[]
    ): Promise<IBoolResult> {
        return this.ResponseHandler({
            cb,
            validators,
            onSuccess: (fs) => {
                if (onSuccess) {
                    this._store.dispatch(onSuccess(fs));
                }
                return BoolResult.createBoolResult_true();
            },
            onFail: (err) => {
                if (onFail) {
                    this._store.dispatch(onFail(undefined));
                }
                if (err instanceof ResponseError) {
                    return BoolResult.createBoolResult2_false(err.code, err.uiMessage);
                }
            },
        });
    }


    // --------------------------------------------------------------------------------------------
    // -- Load config
    // --------------------------------------------------------------------------------------------
    public async loadNavigationConfig(
    ): Promise<IBoolResult> {
        return this.BoolResponseHandler(
            () => this._dataHubService.loadNavigationConfig(),
            navigationStoreConfig,
            () => navigationStoreConfig(NAV_DISPLAY_OPTIONS_INITIAL_STATE),
            [noEmptyResponse]
        );
    }

    // --------------------------------------------------------------------------------------------
    // -- Load/Save elements
    // --------------------------------------------------------------------------------------------

    /**
    * Sendet eine Anfrage zum Laden von Element-Daten passend zur Sucheeingabe.
    *
    * @param keine.
    * @returns [Promise<IBoolResult>]
    */
    public loadElements1stInitial(): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadElements1stInitial(called)'); }
        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsInitial(
                this._store.getState().navigationState.element1stRequestOptions
            ),
            navigationStoreResult1stInit,
            navigationStoreResult1stInitialValue,
            [noEmptyResponse, this.noErrorResponse.bind(this), noEmptyResult, rootOrResultResponse, this.confirmLargeSearch.bind(this)]
        );
    }

    /**
    * Sendet eine Anfrage zum Laden von Element-Daten passend zur Sucheeingabe.
    *
    * @param keine.
    * @returns [Promise<IBoolResult>]
    */
    public loadElements2stInitial(): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadElements2stInitial(called)'); }

        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsInitial(
                this._store.getState().navigationState.element2stRequestOptions
            ),
            navigationStoreResult2stInit,
            navigationStoreResult2stInitialValue,
            [noEmptyResponse, this.noErrorResponse.bind(this), noEmptyResult, rootOrResultResponse, this.confirmLargeSearch.bind(this)]
        );
    }

    private async confirmLargeSearch(fs: any): Promise<ResponseError | undefined> {
        if (fs && fs.childs) {
            let count = 0;
            for (const collection in fs.childs) {
                if (fs.childs.hasOwnProperty(collection)) {
                    count += fs.childs[collection].length;
                }
            }
            if (count > 150) {
                const res = await this._confirmDialogsService.open(
                    'Achtung!',
                    `Der resultierende Graph Ihrer Anfrage beinhaltet sehr viele Elemente.
                        Die Darstellung kann einige Zeit in Anspruch nehmen.
                        Trotzdem fortfahren?`,
                    true,
                    'Abbrechen',
                    'Fortfahren'
                ).toPromise();
                if (res) {
                    return undefined;
                } else {
                    return new ResponseError(
                        'navigation/ABORTED',
                        'Suche wurde abgebrochen.'
                    );
                }
            }
        } else {
            return undefined;
        }
    }

    private async noErrorResponse(fs: any): Promise<ResponseError | undefined> {
        if (isErrorResponse(fs)) {
            switch (fs.code) {
                case 'out-of-bounds':
                    await this._confirmDialogsService.open(
                        'Achtung!',
                        fs.uiMessage,
                        false,
                        'Abbrechen',
                        'Ok'
                    ).toPromise();
                    return new ResponseError(
                        'navigation/ABORTED',
                        'Suche wurde abgebrochen.'
                    );
                default:
                    return new ResponseError(
                        fs.code,
                        fs.uiMessage || 'Unbekannten Fehler vom Server erhalten'
                    );
            }
        }
        return undefined;
    }

    /**
    * Funktion sendet Request zum Nachladen der Knoten-Erweiterung.
    *
    * @param selectedNodeID: string
    * @returns [Promise<IBoolResult>]
    */
    public loadElements1stExtension(
        selectedNodeID: string
    ): Promise<IBoolResult> {
        // sanitize
        const requestOptions = this._store.getState().navigationState.element1stRequestOptions;
        if (!requestOptions) {
            return Promise.reject(new ErrorMessage('NavigationService', 'Kein Search Kontext für Request'));
        }

        // specify request depth 1
        requestOptions.loadOptions = 'expand';

        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions),
            navigationStoreResult1stExtended,
            () => navigationStoreResult1stExtended(null),
            [noEmptyResponse, this.noErrorResponse.bind(this), rootResponse]
        );
    }

    /**
    * Funktion sendet Request zum Nachladen der Knoten-Erweiterung.
    * Im Gegensatz zur Funktion loadElements1stExtension werden die Search Optionen übergeben.
    *
    * @param selectedNodeID: string
    * @param requestOptions: INavRequestOption
    * @returns [Promise<IBoolResult>]
    */
    public loadElements1stExtensionWithOption(
        selectedNodeID: string,
        requestOptions: INavRequestOption
    ): Promise<IBoolResult> {

        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions),
            navigationStoreResult1stExtended,
            () => navigationStoreResult1stExtended(null),
            [noEmptyResponse, this.noErrorResponse.bind(this), rootResponse]
        );
    }

    /**
    * Funktion sendet Request zum Nachladen der Knoten-Erweiterung.
    *
    * @param requestContext: any
    * @returns [Promise<IBoolResult>]
    */
    public loadElements2stExtension(
        selectedNodeID: string
    ): Promise<IBoolResult> {
        const requestOptions: INavRequestOption = this._store.getState().navigationState.element2stRequestOptions;
        if (!requestOptions) {
            return Promise.reject(new ErrorMessage('NavigationService', 'Kein Search Kontext für Request'));
        }

        // specify request depth 1
        requestOptions.loadOptions = 'expand';
        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions),
            navigationStoreResult2stExtended,
            () => navigationStoreResult2stExtended(null),
            [noEmptyResponse, this.noErrorResponse.bind(this), rootResponse]
        );
    }

    /**
    * Funktion sendet Request zum Reduzieren auf Element mit gegebener ID.
    *
    * @param selectedNodeID: string
    * @returns [Promise<IBoolResult>]
    */
    public loadElements1stReduced(
        selectedNodeID: string
    ): Promise<IBoolResult> {
        const requestOptions: INavRequestOption = this._store.getState().navigationState.element1stRequestOptions;
        // sanitize
        if (!requestOptions) {
            return Promise.reject(new ErrorMessage('NavigationService', 'Kein Search Kontext für Request'));
        }

        // specify request depth 2
        requestOptions.loadOptions = 'graph';
        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions),
            navigationStoreResult1stInit,
            navigationStoreResult1stInitialValue,
            [noEmptyResponse, this.noErrorResponse.bind(this), rootResponse]
        );
    }

    /**
    * Funktion sendet Request zum Reduzieren auf Element mit gegebener ID.
    *
    * @param selectedNodeID: string
    * @returns [Promise<IBoolResult>]
    */
    public loadElements2stReduced(
        selectedNodeID: string
    ): Promise<IBoolResult> {
        // sanitize
        const requestOptions: INavRequestOption = this._store.getState().navigationState.element2stRequestOptions;
        if (!requestOptions) {
            return Promise.reject(new ErrorMessage('NavigationService', 'Kein Search Kontext für Request'));
        }

        // specify request depth 2
        requestOptions.loadOptions = 'graph';

        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions),
            navigationStoreResult2stInit,
            navigationStoreResult2stInitialValue,
            [noEmptyResponse, this.noErrorResponse.bind(this), rootResponse]
        );
    }

    public loadElements1stDetail(
        elementID: string,
    ): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadElements1stDetail(called)'); }
        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsDetail(elementID),
            navigationStoreResult1stDetail,
            () => navigationStoreResult1stDetail(null),
            [noEmptyResponse, this.noErrorResponse.bind(this)]
        );
    }

    public loadElements2stDetail(
        elementID: string,
    ): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadElements2stDetail(called)'); }

        return this.BoolResponseHandler(
            () => this._dataHubService.loadElementsDetail(elementID),
            navigationStoreResult2stDetail,
            () => navigationStoreResult2stDetail(null),
            [noEmptyResponse, this.noErrorResponse.bind(this)]
        );

    }

    public loadNode(
        elementID: string,
    ): Promise<INavElementsNode> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadNode(called)'); }

        return this.ResponseHandler({
            cb: () => this._dataHubService.loadElementsDetail(elementID),
            validators: [noEmptyResponse, dataResponse],
            onSuccess: (node) => {
                const [collection] = node.data._id.split('/');
                const config = this._store.getState().navigationState.config;
                const subtypesConfig = config.appearance && config.appearance.subtype;
                const subType = subtypesConfig && subtypesConfig[collection];
                return {
                    id: node.data._id,
                    group: collection,
                    label: node.data.bezeichnung,
                    titel: node.data.anmerkung,
                    typ: node.data[subType],
                    art: node.data.art,
                    display: true,
                    anlage: node.data.anlage,
                } as INavElementsNode;
            }
        });
    }

    public saveElements1stDetail(
        elementDetail: any,
        confirmed?: boolean,
    ): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:saveElements1stDetail(called)'); }
        return this.BoolResponseHandler(
            async () => {
                const doc = await this._dataHubService.saveElementsDetail(elementDetail, confirmed);
                this.loadElements1stDetail(doc._id);
                return Promise.resolve(doc);
            }
        );
    }

    public saveEdge(
        edge: INavElementsEdge
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:saveEdge(called)'); }

        return this.ResponseHandler({
            cb: () => this._dataHubService.saveElementsDetail({
                data: {
                    _key: edge.key,
                    _rev: edge.rev,
                    _id: edge.id,
                    _from: edge.from,
                    _to: edge.to,
                    aktion: parseInt(edge.aktion, 10)
                }
            }),
            onSuccess: (fs) => fs,
        });
    }

    public setDefaults(
        nodeID: string
    ): Promise<any> {
        return this.ResponseHandler({
            cb: () => this._dataHubService.setDefaults(
                nodeID
            ),
            onSuccess: (fs) => fs,
            onFail: (err) => { console.log(err); return undefined; }
        });
    }

    public getCheckers(): Promise<IChecker[]> {
        return this.ResponseHandler({
            cb: () => this._dataHubService.getChecker(),
            onSuccess: (fs) => fs.checkers,
            onFail: (err) => { console.log(err); return undefined; }
        });
    }

    public getReport(id: string): Promise<HttpResponse<Blob>> {
        return this.ResponseHandler<HttpResponse<Blob>, HttpResponse<any>>({
            cb: () => this._dataHubService.getReport(id),
            onSuccess: (fs) => fs,
        });
    }

    public postChecklist(ids: string[]): Promise<any> {
        return this.ResponseHandler({
            cb: () => this._dataHubService.postChecklist(ids),
            onSuccess: (fs) => fs,
            onFail: (err) => {
                console.log(err);
                return undefined;
            }
        });
    }

    public newEdgeAndElement(
        newEdgeAndElement: INavNewEdgeAndElement,
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:newEdgeAndElement(called)'); }

        return this.ResponseHandler({
            cb: () => this._dataHubService.newEdgeAndElement(
                newEdgeAndElement
            ),
            onSuccess: (fs) => fs,
            onFail: (err) => {
                console.log(err);
                return undefined;
            }
        });
    }

    public validateDocument(
        toValidate: {
            collection: string,
            document: any,
        }) {
        return this.ResponseHandler({
            cb: () => this._dataHubService.validateDocument(
                toValidate
            ),
            onSuccess: (fs) => {
                return fs;
            }
            ,
            onFail: (err) => {
                console.log(err);
                return undefined;
            }
        })
    }

    public async loadSchema(
        elementType: string,
    ): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:loadSchema(called)'); }

        try {
            const schemaResponse = await this.inner(
                () => this._dataHubService.loadSchema(elementType),
                [noEmptyResponse, arrayResponse]
            );
            const uiSchemaResponse = await this.inner(
                () => this._dataHubService.loadUiSchema(elementType),
                [noEmptyResponse, arrayResponse]
            );
            // store
            this._store.dispatch(navigationStoreSchema({
                schema: schemaResponse[0],
                layout: uiSchemaResponse[0]
            }));

            // ok
            return BoolResult.createBoolResult_true();
        } catch (err) {
            this._store.dispatch(navigationStoreSchema(null));
            if (err instanceof ResponseError) {
                return BoolResult.createBoolResult2_false(err.code, err.uiMessage);
            }
            throw err;
        }
    }

    // --------------------------------------------------------------------------------------------
    // -- Remove elements
    // --------------------------------------------------------------------------------------------

    /**
    * Sendet request zum Löschen einer Edge zum Backend.
    *
    * @param edgeID: string
    * @returns Promise<IBoolResult>
    */
    public removeEdge(
        edgeID: string
    ): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:removeEdge(called)'); }

        return this.BoolResponseHandler(
            () => this._dataHubService.removeEdge(edgeID)
        );
    }

    public deleteElements(elements: string[]): Promise<IBoolResult> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:deleteElements(called)'); }

        return this.BoolResponseHandler(
            () => this._dataHubService.deleteElements(elements)
        );
    }

    // --------------------------------------------------------------------------------------------
    // -- Add elements
    // --------------------------------------------------------------------------------------------

    /**
    * Sendet request zum Hinzufügen einer Edge zum Backend.
    *
    * @param graph2SelResult: INavGraph2SelResult
    * @returns Promise<{ document: INavElementsEdge, edges?: INavElementsEdge[], edgeCollection?: string }>
    */
    public addEdge(
        graph2SelResult: INavGraph2SelResult,
        createDefaults?: boolean,
        createComponentDefaults?: boolean,
        createFGConnection?: boolean,

    ): Promise<{ document: INavElementsEdge, edges?: INavElementsEdge[], edgeCollection?: string }> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:addEdge(called)'); }

        // FWD - Richtung der Edge
        let from = graph2SelResult.node1Id;
        let to = graph2SelResult.node2Id;
        // BKWD -> reverse
        if (graph2SelResult.edgeDir === EdgeDirectionBKWD) {
            [from, to] = [to, from];
        }

        return this.ResponseHandler({
            cb: () => this._dataHubService.addEdge({
                key: '',
                rev: '',
                id: '',
                from: from,
                to: to,
                group: graph2SelResult.edgeGroup,
                label: '',
                createComponentDefaults,
                createDefaults,
                createFGConnection,
            }),
            onSuccess: (res) => {
                res.document = {
                    key: res.document._key,
                    rev: res.document._rev,
                    id: res.document._id,
                    from: res.document._from,
                    to: res.document._to,
                    group: graph2SelResult.edgeGroup,
                    label: graph2SelResult.edgeGroup,
                    titel: '',
                    display: true
                } as INavElementsEdge;
                if (res.edges) {
                    res.edges = res.edges.map((v) => {
                        return {
                            key: v._key,
                            rev: v._rev,
                            id: v._id,
                            from: v._from,
                            to: v._to,
                            group: 'Schaltet',
                            label: 'Schaltet',
                            titel: '',
                            display: true,
                            aktion: v.aktion,
                        } as INavElementsEdge;
                    });
                }
                return res;
            },
            validators: [noEmptyResponse]
        });
    }

    // --------------------------------------------------------------------------------------------
    // -- Steuerszenarien
    // --------------------------------------------------------------------------------------------

    /**
    * Sendet request mit Änderung beim Schlatungszustand einer Komponente zum Backend.
    *
    * @param edge: INavElementsEdge
    * @returns Promise<INavElementsEdge>
    */
    public addEdgeWithAktion(
        edge: INavElementsEdge
    ): Promise<INavElementsEdge> {
        if (navigationConfig.debugMode) { console.log('[NavigationService]:addEdgeWithSwitchValue(called)'); }

        return this.ResponseHandler({
            cb: () => this._dataHubService.addEdge(edge),
            validators: [noEmptyResponse],
            onSuccess: (res) => {
                res.document = {
                    key: res.document._key,
                    rev: res.document._rev,
                    id: res.document._id,
                    from: res.document._from,
                    to: res.document._to,
                    group: 'Schaltet',
                    label: '',
                    titel: '',
                    aktion: res.aktion,
                    display: true
                } as INavElementsEdge;
                return res.document;
            }
        });
    }

    public async getRawGraphdata(
        selectedNodeID: string,
        requestOptions: any
    ) {
        return await this._dataHubService.loadElementsExtention(selectedNodeID, requestOptions)
            .then((result) => result).catch(() => undefined);
    }

}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/service/navigation.service.ts